import { cva } from 'class-variance-authority'
import { ChangeEvent, InputHTMLAttributes, useCallback, useEffect, useState } from 'react'

import { cn } from '@/utils/style'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  value?: number | string
  disabled?: boolean
  allowClear?: boolean
  onChange?: (value: ChangeEvent<HTMLInputElement>) => void
  onClear?: () => void
  onPressEnter?: (value: string) => void
  validator?: (value: string) => boolean
  className?: string
}

const inputVariants = cva(
  'w-full h-12 leading-12 px-4 rounded-lg border-[2px] border-gray100 bg-panel-white text-primary placeholder:text-tertiary outline-none focus:border-secondary',
  {
    variants: {
      disabled: {
        true: 'cursor-not-allowed bg-panel-gray-100 text-tertiary',
      },
      error: {
        true: 'border-red-500 focus:border-red-500',
      },
    },
  },
)

export function Input({
  value,
  onChange,
  onClear,
  onPressEnter,
  disabled = false,
  allowClear = false,
  validator,
  className,
  ...rest
}: InputProps) {
  const [v, setV] = useState(value)
  const [error, setError] = useState(false)

  useEffect(() => {
    setV(value)
  }, [value])

  const onVChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setV(newValue)
      setError(validator ? !validator(newValue) : false)

      if (onChange) {
        onChange(e)
      }
    },
    [onChange],
  )

  const handleClear = useCallback(() => {
    setV('')
    setError(false)
    if (onClear) {
      onClear()
    }
  }, [onClear])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e?.key === 'Enter' && onPressEnter && typeof v === 'string') {
        onPressEnter(v)
      }
    },
    [v, onPressEnter],
  )

  return (
    <div className="relative">
      <input
        value={v}
        onChange={onVChange}
        onKeyDown={handleKeyDown}
        className={cn(inputVariants({ disabled, error }), className)}
        disabled={disabled}
        {...rest}
      />
      {allowClear && !disabled && v && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary hover:text-secondary"
        >
          ✕
        </button>
      )}
    </div>
  )
}
